<template>
    <generic-page>
        <div class="d-flex align-items-center justify-content-between">
            <b-btn variant="link" size="lg" class="pl-0" to="/creator/creatorVideo">
                <font-awesome-icon size="lg" icon="fa-light fa-chevron-left" />
                All Videos
            </b-btn>
            <b-dropdown
                v-if="creatorVideo"
                right
                no-caret
                variant="link"
            >
                <template #button-content>
                    <font-awesome-icon
                        icon="fa-solid fa-ellipsis"
                        class="text-secondary"
                    />
                </template>
                <b-dropdown-item
                    @click="deleteVideo"
                    link-class="text-danger"
                >
                    <font-awesome-icon icon="fa-light fa-trash" class="mr-1" />
                    Delete Video
                </b-dropdown-item>
            </b-dropdown>
        </div>

        <div v-if="creatorVideo">
            <b-alert
                v-if="creatorVideo.published && !creatorVideo.approved"
                variant="warning"
                show
                class="d-flex align-items-center mb-3"
            >
                <font-awesome-icon
                    icon="fa-duotone fa-clock"
                    class="mr-2"
                    size="lg"
                />
                <div>
                    <strong>Pending Review</strong>
                    <div class="small">
                        Your video is being reviewed and will be visible once
                        approved.
                    </div>
                </div>
            </b-alert>

            <b-alert v-if="error" variant="danger" show>
                {{ error }}
            </b-alert>

            <div v-if="!creatorVideo.processed">
                <creator-video-upload />
            </div>
            <div v-else>
                <div class="video-header">
                    <div class="video-thumb-wrap">
                        <img
                            :src="
                                creatorVideo.previewUrl ||
                                creatorVideo.thumbnailUrl
                            "
                            class="video-thumb"
                        />
                        <div v-if="formattedDuration" class="thumb-duration">
                            {{ formattedDuration }}
                        </div>
                    </div>
                    <div class="video-meta">
                        <div class="d-flex align-items-center mb-1">
                            <b-badge :variant="statusVariant" pill class="mr-2">
                                {{ statusLabel }}
                            </b-badge>
                        </div>
                        <div
                            v-if="creatorVideo.message"
                            class="video-meta-message text-truncate"
                        >
                            {{ creatorVideo.message }}
                        </div>
                        <div class="text-secondary small mt-1">
                            {{ creatorVideo.publishDateTerse || "Not scheduled" }}
                        </div>
                        <a
                            :href="creatorVideo.mp4Url"
                            target="_blank"
                            class="small mt-2 d-inline-block"
                        >
                            <font-awesome-icon
                                icon="fa-light fa-arrow-up-right-from-square"
                                class="mr-1"
                            />
                            View Original
                        </a>
                    </div>
                </div>

                <div v-if="!creatorVideo.published" class="draft-notice">
                    <div class="draft-notice-icon">
                        <font-awesome-icon
                            icon="fa-duotone fa-paper-plane"
                            size="2x"
                            class="text-primary"
                        />
                    </div>
                    <div class="draft-notice-text">
                        <strong>Ready to go?</strong>
                        <div class="small text-secondary">
                            Once you submit, our team will review your video.
                            You'll be notified when it's approved and visible
                            to customers.
                        </div>
                    </div>
                    <b-btn
                        variant="primary"
                        @click="publishPost"
                        pill
                        class="mt-2"
                        block
                    >
                        Submit for Review
                    </b-btn>
                </div>

                <div class="section-label">Details</div>
                <b-list-group flush class="studio-list">
                    <b-list-group-item class="studio-item">
                        <div class="studio-item-label">Status</div>
                        <div class="studio-item-value">
                            <b-badge :variant="statusVariant" pill>
                                {{ statusLabel }}
                            </b-badge>
                        </div>
                    </b-list-group-item>
                    <b-list-group-item
                        button
                        class="studio-item"
                        :disabled="!creatorVideo.editable"
                        @click="scheduleModal = true"
                    >
                        <div class="studio-item-label">Schedule</div>
                        <div class="studio-item-value">
                            <span class="mr-1">
                                {{ creatorVideo.publishDateTerse || "Now" }}
                            </span>
                            <font-awesome-icon
                                v-if="creatorVideo.editable"
                                icon="fa-light fa-chevron-right"
                                class="text-secondary"
                            />
                        </div>
                    </b-list-group-item>
                    <b-list-group-item class="studio-item studio-item-message">
                        <div
                            v-if="!editingMessage"
                            class="d-flex align-items-center justify-content-between w-100"
                            @click="startEditingMessage"
                            style="cursor: pointer"
                        >
                            <div class="studio-item-label">Message</div>
                            <div class="studio-item-value">
                                <span
                                    v-if="creatorVideo.message"
                                    class="message-preview"
                                >
                                    {{ creatorVideo.message }}
                                </span>
                                <span v-else class="text-secondary">
                                    Add a message
                                </span>
                                <font-awesome-icon
                                    icon="fa-light fa-pencil"
                                    class="text-secondary ml-2"
                                />
                            </div>
                        </div>
                        <div v-else class="w-100">
                            <b-form-textarea
                                ref="messageInput"
                                v-model="editMessage"
                                placeholder="Add a message..."
                                rows="2"
                                max-rows="5"
                                maxlength="255"
                                size="sm"
                                class="mb-2"
                            />
                            <div class="d-flex justify-content-end gap-2">
                                <b-btn
                                    size="sm"
                                    variant="light"
                                    @click="cancelEditingMessage"
                                    class="mr-1"
                                >
                                    Cancel
                                </b-btn>
                                <b-btn
                                    size="sm"
                                    variant="primary"
                                    @click="saveMessage"
                                >
                                    Save
                                </b-btn>
                            </div>
                        </div>
                    </b-list-group-item>
                </b-list-group>

                <b-modal
                    v-model="scheduleModal"
                    hide-footer
                    centered
                    title="Schedule Post"
                >
                    <b-calendar
                        @selected="setScheduleDate"
                        v-model="scheduledDate"
                        :min="minScheduleDate"
                        :max="maxScheduleDate"
                        block
                    />
                </b-modal>

                <div class="section-label">Share & Promote</div>
                <b-list-group flush class="studio-list">
                    <b-list-group-item
                        button
                        class="studio-item"
                        :disabled="!creatorVideo.approved"
                        @click="goToShare"
                    >
                        <div class="studio-item-label">
                            <font-awesome-icon
                                icon="fa-duotone fa-share"
                                class="studio-icon text-primary"
                            />
                            Share Link & QR
                        </div>
                        <div class="studio-item-value">
                            <font-awesome-icon
                                v-if="creatorVideo.approved"
                                icon="fa-light fa-chevron-right"
                                class="text-secondary"
                            />
                            <b-badge v-else variant="light" pill>
                                Pending
                            </b-badge>
                        </div>
                    </b-list-group-item>
                    <b-list-group-item
                        v-if="!hasBroadcasted"
                        button
                        class="studio-item"
                        :disabled="!creatorVideo.approved"
                        @click="goToBroadcast"
                    >
                        <div class="studio-item-label">
                            <font-awesome-icon
                                icon="fa-duotone fa-bullhorn"
                                class="studio-icon text-info"
                            />
                            Broadcast to Followers
                        </div>
                        <div class="studio-item-value">
                            <template v-if="creatorVideo.approved">
                                <coin-badge :amount="1" />
                                <font-awesome-icon
                                    icon="fa-light fa-chevron-right"
                                    class="text-secondary ml-2"
                                />
                            </template>
                            <b-badge v-else variant="light" pill>
                                Pending
                            </b-badge>
                        </div>
                    </b-list-group-item>
                    <b-list-group-item
                        v-else
                        class="studio-item"
                    >
                        <div class="studio-item-label">
                            <font-awesome-icon
                                icon="fa-light fa-check-circle"
                                class="studio-icon text-success"
                            />
                            Broadcasted
                        </div>
                        <div class="studio-item-value text-secondary small">
                            Sent
                        </div>
                    </b-list-group-item>
                    <b-list-group-item
                        button
                        class="studio-item"
                        :disabled="!creatorVideo.approved"
                        @click="goToInvite"
                    >
                        <div class="studio-item-label">
                            <font-awesome-icon
                                icon="fa-duotone fa-envelope"
                                class="studio-icon text-success"
                            />
                            Invite Customer
                        </div>
                        <div class="studio-item-value">
                            <template v-if="creatorVideo.approved">
                                <coin-badge :amount="1" />
                                <font-awesome-icon
                                    icon="fa-light fa-chevron-right"
                                    class="text-secondary ml-2"
                                />
                            </template>
                            <b-badge v-else variant="light" pill>
                                Pending
                            </b-badge>
                        </div>
                    </b-list-group-item>
                </b-list-group>

                <div class="section-label">Insights</div>
                <b-list-group flush class="studio-list">
                    <b-list-group-item class="studio-item">
                        <div class="studio-item-label">
                            <font-awesome-icon
                                icon="fa-duotone fa-trophy"
                                class="studio-icon text-warning"
                            />
                            Current Ranking
                        </div>
                        <div class="studio-item-value">
                            <b-badge
                                v-if="currentRanking"
                                variant="warning"
                                pill
                            >
                                #{{ currentRanking }}
                            </b-badge>
                            <span v-else class="text-secondary">—</span>
                        </div>
                    </b-list-group-item>
                    <b-list-group-item
                        button
                        class="studio-item"
                        @click="goToUpvotes"
                    >
                        <div class="studio-item-label">
                            <font-awesome-icon
                                icon="fa-duotone fa-thumbs-up"
                                class="studio-icon text-primary"
                            />
                            Upvotes
                        </div>
                        <div class="studio-item-value">
                            {{ upvotedCount }}
                            <font-awesome-icon
                                icon="fa-light fa-chevron-right"
                                class="text-secondary ml-2"
                            />
                        </div>
                    </b-list-group-item>
                </b-list-group>

            </div>

        </div>
        <div v-else>
            <generic-loading />
        </div>
    </generic-page>
</template>

<style scoped>
.video-header {
    display: flex;
    gap: 14px;
    padding: 12px 0;
}

.video-thumb-wrap {
    position: relative;
    flex-shrink: 0;
    width: 90px;
    border-radius: 8px;
    overflow: hidden;
    background: #1a1a1a;
}

.video-thumb {
    width: 100%;
    aspect-ratio: 9 / 16;
    object-fit: cover;
}

.thumb-duration {
    position: absolute;
    bottom: 4px;
    right: 4px;
    background: rgba(0, 0, 0, 0.7);
    color: #fff;
    font-size: 0.65rem;
    font-weight: 600;
    padding: 1px 5px;
    border-radius: 3px;
}

.video-meta {
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
}

.video-meta-message {
    font-size: 0.9rem;
    color: #e2e8f0;
}

.draft-notice {
    text-align: center;
    padding: 20px 16px;
    margin: 8px 0;
    border-radius: 10px;
    background: rgba(99, 102, 241, 0.08);
    border: 1px solid rgba(99, 102, 241, 0.15);
}

.draft-notice-icon {
    margin-bottom: 8px;
}

.draft-notice-text {
    margin-bottom: 4px;
}

.section-label {
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #64748b;
    padding: 16px 0 6px;
}

.studio-list {
    border-radius: 10px !important;
    overflow: hidden;
}

.studio-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
}

.studio-item-label {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 0.9rem;
}

.studio-item-value {
    font-size: 0.85rem;
}

.studio-icon {
    width: 20px;
    text-align: center;
}

.studio-item-message {
    display: block;
}

.message-preview {
    max-width: 180px;
    display: inline-block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: middle;
}
</style>

<script>
import { mapState } from "vuex";

import CreatorVideoUpload from "@/components/creator/video/CreatorVideoUpload";

export default {
    data() {
        return {
            scheduleModal: false,
            editingMessage: false,
            editMessage: "",
            scheduledDate: null,
        };
    },
    created() {
        if (this.$route.params?.id) {
            this.$store.dispatch("creatorVideo/get", {
                creatorVideoId: this.$route.params?.id,
            });
        }
    },
    watch: {
        $route(to) {
            if (
                to?.params?.id !==
                this.$store.state.creatorVideo.creatorVideo?.id
            ) {
                this.$store.dispatch("creatorVideo/get", {
                    creatorVideoId: this.$route.params?.id,
                });
            }
        },
    },
    computed: {
        formattedDuration() {
            if (!this.creatorVideo?.duration) return null;
            const d = this.creatorVideo.duration;
            const min = Math.floor(d / 60);
            const sec = d % 60;
            return `${min}:${String(sec).padStart(2, "0")}`;
        },
        statusLabel() {
            if (this.creatorVideo.approved) return "Live";
            if (this.creatorVideo.published) return "Pending Review";
            return "Draft";
        },
        statusVariant() {
            if (this.creatorVideo.approved) return "success";
            if (this.creatorVideo.published) return "warning";
            return "secondary";
        },
        minScheduleDate() {
            return new Date().toISOString().slice(0, 10);
        },
        maxScheduleDate() {
            const d = new Date();
            d.setMonth(d.getMonth() + 1);
            return d.toISOString().slice(0, 10);
        },
        ...mapState("creatorVideo", [
            "creatorVideo",
            "hasBroadcasted",
            "upvotedCount",
            "currentRanking",
            "recentlySaved",
            "error",
        ]),
    },
    methods: {
        publishPost() {
            this.$store.dispatch("creatorVideo/publish");
        },
        deleteVideo() {
            this.$store.dispatch("creatorVideo/delete");
        },
        startEditingMessage() {
            this.editMessage = this.creatorVideo.message || "";
            this.editingMessage = true;
            this.$nextTick(() => {
                this.$refs.messageInput?.focus();
            });
        },
        cancelEditingMessage() {
            this.editingMessage = false;
        },
        saveMessage() {
            this.api
                .post(
                    `/creatorVideo/${this.creatorVideo.id}/updateMessage`,
                    { message: this.editMessage }
                )
                .then((response) => {
                    if (response?.data?.creatorVideo) {
                        this.$store.commit(
                            "creatorVideo/setCreatorVideo",
                            response.data.creatorVideo
                        );
                    }
                });
            this.editingMessage = false;
        },
        setScheduleDate() {
            this.api
                .post(
                    `/creatorVideo/${this.creatorVideo.id}/updatePublishedAt`,
                    { publishedAt: this.scheduledDate }
                )
                .then((response) => {
                    if (response?.data?.creatorVideo) {
                        this.$store.commit(
                            "creatorVideo/setCreatorVideo",
                            response.data.creatorVideo
                        );
                    }
                });
            this.scheduleModal = false;
        },
        goToShare() {
            this.$router.push({
                name: "creatorVideoShare",
                params: { id: this.creatorVideo.id },
            });
        },
        goToUpvotes() {
            this.$router.push({
                name: "creatorVideoUpvotes",
                params: { id: this.creatorVideo.id },
            });
        },
        goToInvite() {
            this.$router.push({
                name: "creatorVideoInvite",
                params: { id: this.creatorVideo.id },
            });
        },
        goToBroadcast() {
            this.$router.push({
                name: "creatorVideoBroadcast",
                params: { id: this.creatorVideo.id },
            });
        },
    },
    components: {
        CreatorVideoUpload,
    },
};
</script>
